import React, { useEffect, useState, useCallback, Dispatch, SetStateAction, useRef } from "react";
import { withRetry } from './components/request';
import { createPortal } from "react-dom";
import "./Grid.css";

type Props = {
  username: string;
  userColor: string;
  showSettings: boolean;
  setShowSettings: Dispatch<SetStateAction<boolean>>;
  handleLogout: () => void;
  setUsername: Dispatch<SetStateAction<string | null>>;
  tokenBalance: number;
  setTokenBalance: Dispatch<SetStateAction<number>>;
  blockData: Block[];
  setBlockData: Dispatch<SetStateAction<Block[]>>;
};

type Block = {
  index: number;
  dugBy: string | null;
  color?: string | null;
  visual?: string | null;
  // PoW-related fields (optional)
  height?: number;
  nonce?: number;
  hash?: string;
};

type BlockState = "idle" | "digging" | "dug";
type MiningStatus = 'idle' | 'fetching_challenge' | 'mining' | 'submitting' | 'success' | 'error';

// POW endpoint (Nginx'te /pow/ --> 4001'e gidiyor)
const POW_API = '/pow';

// Kullanıcı pubkey'ini al (64 hex bekliyoruz, yoksa dummy)
const getUserPubkeyHex = () => {
  const v = localStorage.getItem('user_pubkey') || '';
  return /^[0-9a-fA-F]{64}$/.test(v) ? v : '0'.repeat(64);
};

// kickPowShadow removed (unused)

// Grid is always 10x10
const API_BASE = "";
const USE_POW_MINING = false; // Set to true to enable PoW mining, false for 10s timer

const Grid: React.FC<Props> = ({ username, userColor, showSettings, setShowSettings, handleLogout, setUsername, tokenBalance, setTokenBalance, blockData, setBlockData }) => {
  const [blockStates, setBlockStates] = useState<BlockState[]>([]);
  const [miningStatus, setMiningStatus] = useState<MiningStatus>('idle');
  const [isMining, setIsMining] = useState<boolean>(false);
  const [selectedBlockIndex, setSelectedBlockIndex] = useState<number | null>(null);
  const [newlyDugBlocks, setNewlyDugBlocks] = useState<Set<number>>(new Set());
  const [dailyLimitFailedBlocks, setDailyLimitFailedBlocks] = useState<Set<number>>(new Set());
  const [blockInfo, setBlockInfo] = useState<string>('');
  const [isGridLoading, setIsGridLoading] = useState<boolean>(true);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const autoLoadCountRef = useRef<number>(0);
  
  // Block Info Modal State
  const [showBlockModal, setShowBlockModal] = useState<boolean>(false);
  const blockRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isInitialLoad = useRef(true);
  const hasScrolledInitial = useRef(false);
  // removed unused hasAutoScrolled ref
  
  // Get safe modal width based on screen size
  const getModalWidth = () => {
    const screenWidth = window.screen.width || window.innerWidth;
    return screenWidth < 400 ? '280px' : '300px';
  };
  
  // Device detection
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /android/i.test(navigator.userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  // Aggressive modal CSS protection with Safari-specific handling
  useEffect(() => {
    if (!showBlockModal) return;
    
    // Lock body scroll on modal open (Safari fix)
    const originalBodyStyle = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    
    const forceModalCSS = () => {
      // Find modal elements
      const overlay = document.querySelector('[style*="z-index: 99999"]');
      const content = document.querySelector('[style*="border-radius: 12px"][style*="background"]');
      
      if (overlay) {
        const overlayEl = overlay as HTMLElement;
        overlayEl.style.position = 'fixed';
        overlayEl.style.top = '0px';
        overlayEl.style.left = '0px';
        
        if (isIOS) {
          // iOS (Safari + Chrome): Use screen dimensions instead of viewport units
          overlayEl.style.width = `${window.screen.width}px`;
          overlayEl.style.height = `${window.screen.height}px`;
          overlayEl.style.right = '0px';
          overlayEl.style.bottom = '0px';
          overlayEl.style.setProperty('-webkit-transform', 'none');
          overlayEl.style.setProperty('-webkit-zoom', 'normal');
          overlayEl.style.setProperty('zoom', 'normal');
        } else if (isAndroid) {
          // Android-specific: Use visualViewport if available, fallback to window
          const viewportHeight = window.visualViewport?.height || window.innerHeight;
          overlayEl.style.width = '100vw';
          overlayEl.style.height = `${viewportHeight}px`;
          overlayEl.style.setProperty('zoom', '1');
          overlayEl.style.setProperty('transform', 'none');
        } else {
          // Desktop and other browsers
          overlayEl.style.width = '100vw';
          overlayEl.style.height = '100vh';
          overlayEl.style.setProperty('zoom', '1');
        }
        
        overlayEl.style.transform = 'none';
        overlayEl.style.zIndex = '2147483647';
        overlayEl.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
        overlayEl.style.display = 'flex';
        overlayEl.style.justifyContent = 'center';
        overlayEl.style.alignItems = 'center';
      }
      
      if (content) {
        const contentEl = content as HTMLElement;
        const modalWidth = isIOS ? (window.screen.width < 400 ? '280px' : '300px') : getModalWidth();
        
        contentEl.style.width = modalWidth;
        contentEl.style.minWidth = modalWidth;
        contentEl.style.maxWidth = modalWidth;
        contentEl.style.transform = 'none';
        contentEl.style.fontSize = '14px';
        contentEl.style.position = 'relative';
        contentEl.style.backgroundColor = 'white';
        contentEl.style.padding = '20px';
        contentEl.style.borderRadius = '12px';
        contentEl.style.textAlign = 'center';
        contentEl.style.margin = '0';
        contentEl.style.border = 'none';
        contentEl.style.outline = 'none';
        
        if (isIOS) {
          contentEl.style.setProperty('-webkit-transform', 'none');
          contentEl.style.setProperty('-webkit-zoom', 'normal');
          contentEl.style.setProperty('zoom', 'normal');
          contentEl.style.setProperty('will-change', 'auto');
        } else {
          contentEl.style.setProperty('zoom', '1');
        }
      }
    };
    
    // Force CSS with requestAnimationFrame for better performance
    let rafId: number;
    const forceModalCSSWithRAF = () => {
      forceModalCSS();
      if (showBlockModal) {
        rafId = requestAnimationFrame(forceModalCSSWithRAF);
      }
    };
    
    // Initial force and start RAF loop
    forceModalCSS();
    if (isSafari) {
      rafId = requestAnimationFrame(forceModalCSSWithRAF);
    }
    
    // MutationObserver to watch for style changes
    const observer = new MutationObserver(forceModalCSS);
    
    // Start observing after modal appears
    setTimeout(() => {
      const overlay = document.querySelector('[style*="z-index: 99999"]');
      const content = document.querySelector('[style*="border-radius: 12px"][style*="background"]');
      
      if (overlay) {
        observer.observe(overlay, { 
          attributes: true, 
          attributeFilter: ['style', 'class'] 
        });
      }
      
      if (content) {
        observer.observe(content, { 
          attributes: true, 
          attributeFilter: ['style', 'class'] 
        });
      }
    }, 10);
    
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      observer.disconnect();
      // Restore body scroll
      document.body.style.overflow = originalBodyStyle;
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [showBlockModal, isAndroid, isIOS, isSafari]);

  // User colors fetched from auth service per username
  const [userColors, setUserColors] = useState<{ [username: string]: string }>({});

  // Pagination state for lazy loading
  const CHUNK_SIZE = 1000;
  const pageStartRef = useRef<number>(0); // start index (inclusive) of current slice in global grid
  const totalRef = useRef<number>(0);
  const isLoadingMoreRef = useRef<boolean>(false);

  const setBlocksFromSlice = useCallback((items: Block[], total: number, start: number, end: number) => {
    // Newest at top: render descending by global index
    const ordered = items.slice().sort((a, b) => (b.index ?? 0) - (a.index ?? 0));
    const newStates: BlockState[] = ordered.map((block) => block.dugBy ? 'dug' : 'idle');
    setBlockStates(newStates);
    setBlockData(ordered);
    pageStartRef.current = start;
    totalRef.current = total;
    setIsGridLoading(false);

    // On initial tail load, jump to bottom so the newest blocks are in view
    const isTail = start === Math.max(0, total - items.length);
    if (isTail && !hasScrolledInitial.current) {
      hasScrolledInitial.current = true;
      // defer to after paint
      const scrollBottom = () => {
        try {
          const h = document.documentElement.scrollHeight || document.body.scrollHeight || 0;
          if (h > 0) {
            window.scrollTo({ top: h });
          }
        } catch {}
      };
      if ('requestAnimationFrame' in window) {
        requestAnimationFrame(() => requestAnimationFrame(scrollBottom));
      } else {
        setTimeout(scrollBottom, 0);
      }
    }
  }, [setBlockStates, setBlockData, setIsGridLoading]);

  const applyDigSuccess = useCallback((arrayIndex: number, globalIdx: number) => {
    setNewlyDugBlocks(prev => new Set(prev).add(globalIdx));
    setTimeout(() => {
      setNewlyDugBlocks(prev => {
        const next = new Set(prev);
        next.delete(globalIdx);
        return next;
      });
    }, 6000);
    setBlockData(prev => {
      const next = prev.slice();
      const block = next[arrayIndex];
      if (!block) return prev;
      next[arrayIndex] = {
        ...block,
        dugBy: username,
        color: userColor,
        visual: block.visual ?? null,
      };
      return next;
    });
    setBlockStates(prev => {
      const next = prev.slice();
      if (arrayIndex < next.length) next[arrayIndex] = 'dug';
      return next;
    });
    setUserColors(prev => ({ ...prev, [username]: userColor }));
  }, [username, userColor, setBlockData]);

  const fetchGrid = useCallback(async () => {
    try {
      const response = await withRetry(`${API_BASE}/grid?tail=${CHUNK_SIZE}&meta=1&_=${Date.now()}`, { method: 'GET' }, 1);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const json = await response.json();
      const items: Block[] = json?.items || json?.grid || json || [];
      const total: number = Number(json?.total ?? items.length);
      const start: number = Number(json?.start ?? Math.max(0, total - items.length));
      const end: number = Number(json?.end ?? total);
      if (json?.colors && typeof json.colors === 'object') {
        setUserColors(json.colors as { [username: string]: string });
      }
      setBlocksFromSlice(items, total, start, end);

      // Update token balance from backend summary (avoid client-side counting over partial slice)
      try {
        const sumRes = await withRetry(`/api/volore/${username}`, { method: 'GET' }, 1);
        if (sumRes.ok) {
          const sum = await sumRes.json();
          setTokenBalance(Number(sum?.total || 0));
        }
      } catch {}
    } catch {}
  }, [username, setTokenBalance, setBlocksFromSlice]);

  const loadMoreOlder = useCallback(async () => {
    if (isLoadingMoreRef.current) return;
    
    // Calculate current start based on visible blocks, not pageStartRef
    const visibleBlocks = blockData;
    if (visibleBlocks.length === 0) return;
    
    // Find the oldest (lowest index) block currently loaded
    const oldestIndex = Math.min(...visibleBlocks.map(b => b.index || 0));
    const currentStart = oldestIndex;
    
    if (currentStart <= 0) {
      console.log('📊 LoadMoreOlder: Reached start of grid, no more data');
      return; // no older data
    }
    
    console.log(`📊 LoadMoreOlder: Starting from ${currentStart}, loading ${CHUNK_SIZE} blocks`);
    isLoadingMoreRef.current = true;
    const prevScrollHeight = document.documentElement.scrollHeight;
    const prevScrollY = window.scrollY;
    try {
      const limit = CHUNK_SIZE;
      const resp = await withRetry(`${API_BASE}/grid?before=${currentStart}&limit=${limit}&meta=1`, { method: 'GET' }, 1);
      if (!resp.ok) return;
      const json = await resp.json();
      const itemsRaw: Block[] = json?.items || [];
      const total: number = Number(json?.total ?? totalRef.current);
      const items = itemsRaw.slice().sort((a, b) => (b.index ?? 0) - (a.index ?? 0));
      const start: number = Number(json?.start ?? Math.max(0, currentStart - items.length));
      const end: number = Number(json?.end ?? currentStart);
      if (items.length === 0) {
        console.log('📊 LoadMoreOlder: No items returned from API');
        return;
      }
      
      console.log(`📊 LoadMoreOlder: Fetched ${items.length} items (${start}-${end}), oldest: ${Math.min(...items.map(b => b.index || 0))}`);

      // Append older items at bottom and de-duplicate by global index
      const existing = blockData;
      const seen = new Set<number>(existing.map(b => b.index).filter(n => typeof n === 'number'));
      const merged: Block[] = existing.slice();
      for (const b of items) {
        if (typeof b.index === 'number' && !seen.has(b.index)) {
          seen.add(b.index);
          merged.push(b);
        }
      }
      const newStates: BlockState[] = merged.map((block) => block.dugBy ? 'dug' : 'idle');
      setBlockStates(newStates);
      setBlockData(merged);
      // Update pageStartRef to the actual oldest loaded block
      const actualOldest = Math.min(...merged.map(b => b.index || 0));
      pageStartRef.current = actualOldest;
      totalRef.current = total;

      // Preserve visual position after prepending
      const newScrollHeight = document.documentElement.scrollHeight;
      const delta = newScrollHeight - prevScrollHeight;
      // For append-at-bottom we keep user's current position
      if (delta > 0) {
        window.scrollTo({ top: prevScrollY });
      }
    } catch {}
    finally {
      isLoadingMoreRef.current = false;
    }
  }, [blockData, setBlockData]);

  // IntersectionObserver to auto-load when reaching the bottom sentinel
  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Trigger load of older blocks when user reaches bottom
          loadMoreOlder();
        }
      });
    }, { root: null, rootMargin: '200px', threshold: 0 });
    observer.observe(el);
    return () => { try { observer.disconnect(); } catch {} };
  }, [loadMoreOlder]);

  // Auto-fill viewport on initial loads while more data exists
  useEffect(() => {
    if (isGridLoading) return;
    // If content height is still short, try preloading a few chunks automatically
    const docH = document.documentElement.scrollHeight || 0;
    const winH = window.innerHeight || 0;
    const hasMore = pageStartRef.current > 0;
    if (hasMore && docH <= winH + 200 && autoLoadCountRef.current < 5) {
      autoLoadCountRef.current++;
      loadMoreOlder();
    }
  }, [blockData, isGridLoading, loadMoreOlder]);

  // Infinite scroll upwards: load older when near top
  useEffect(() => {
    const lastLoadTs = { t: 0 } as { t: number };
    const isInteractingRef = { current: false } as { current: boolean };

    const onPointerDown = () => { isInteractingRef.current = true; };
    const onPointerUp = () => {
      setTimeout(() => { isInteractingRef.current = false; }, 250);
    };
    const onScroll = () => {
      if (isInteractingRef.current) return;
      const nearBottom = (window.scrollY + window.innerHeight) > (document.documentElement.scrollHeight - 120);
      if (nearBottom) {
        const now = performance.now();
        if (now - lastLoadTs.t < 800) return;
        lastLoadTs.t = now;
        loadMoreOlder();
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('pointerup', onPointerUp, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [loadMoreOlder]);

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      fetchGrid();
    }
  }, [fetchGrid]);

  // Remove duplicate API call - token balance is already updated in fetchGrid

  useEffect(() => {
    // Fetch colors of all users in grid from auth service (single source of truth)
    const uniqueUsers = Array.from(new Set(blockData.filter(b => b.dugBy).map(b => b.dugBy)));
    Promise.all(uniqueUsers.map(async (u) => {
      const res = await withRetry(`/auth/user?username=${u}`, { method: 'GET' }, 1);
      const data = await res.json();
      return [u, data.color];
    })).then(results => {
      const colorMap: { [username: string]: string } = {};
      results.forEach(([u, color]) => { colorMap[u as string] = color as string; });
      setUserColors(colorMap);
    }).catch(() => {
      // Fallback: at least set current user's color
      setUserColors({ [username]: userColor });
    });
  }, [blockData, username, userColor]);

  // Auto-focus disabled per performance and UX request

  const handleBlockClick = async (index: number) => {
    setSelectedBlockIndex(index);
    setShowBlockModal(true);
    setBlockInfo('Loading...');
    
    // Fetch hash or miner address for this block
    if (blockData[index] && blockData[index].dugBy) {
      try {
        const globalIdx = (blockData[index] && typeof blockData[index].index === 'number') ? blockData[index].index : index;
        const digId = `dig:${blockData[index].dugBy}:${globalIdx}`;
        // Defer heavy fetch work until after paint to reduce INP
        await new Promise<void>((resolve) => {
          if ('requestIdleCallback' in window) {
            (window as any).requestIdleCallback(() => resolve(), { timeout: 300 });
          } else {
            setTimeout(() => resolve(), 150);
          }
        });
        const response = await fetch(`/volchain/events?limit=1500`);
        const data = await response.json();
        const events = data.events || [];
        
        // Try to find by dig_id (new format with memo)
        console.log(`Looking for digId: ${digId} in ${events.length} events`);
        const digEvents = events.filter((e: any) => e.type === 'mint' && e.reason === 'dig' && e.memo?.dig_id);
        console.log(`Found ${digEvents.length} dig events with dig_id:`, digEvents.map((e: any) => e.memo.dig_id));
        
        const mintEvent = events.find((e: any) => 
          e.type === 'mint' && 
          e.reason === 'dig' && 
          e.memo?.dig_id === digId
        );
        
        console.log(`Found mint event for ${digId}:`, mintEvent);
        
        if (mintEvent && mintEvent.hash) {
          // Found specific hash for this block - make it clickable
          const hashShort = `${mintEvent.hash.slice(0,8)}...${mintEvent.hash.slice(-8)}`;
          setBlockInfo(`<a href="/tx/${mintEvent.hash}" style="color: #2196f3; text-decoration: none;">Tx Hash: ${hashShort}</a>`);
        } else {
          // For old blocks, show original miner's Volchain address - make it clickable
          const anyUserMint = events.find((e: any) => 
            e.type === 'mint' && 
            e.username === blockData[index].dugBy
          );
          
          if (anyUserMint && anyUserMint.pubkey) {
            const addr = anyUserMint.pubkey;
            const addrShort = `${addr.slice(0,8)}...${addr.slice(-8)}`;
            setBlockInfo(`<a href="/address/${addr}" style="color: #2196f3; text-decoration: none;">Miner Addr: ${addrShort}</a>`);
          } else {
            setBlockInfo('Original miner');
          }
        }
      } catch (error) {
        console.error('Failed to fetch block info:', error);
        setBlockInfo('Failed to load');
      }
    }
  };

  const handleDigBlock = async (index: number) => {
    if (isMining || !username) return;

    if (USE_POW_MINING) {
      if (miningStatus !== 'idle') return;

      setShowBlockModal(false);
      setIsMining(true);
      setMiningStatus('fetching_challenge');

      try {
        // 1. Fetch the mining challenge from our new PoW service
        const targetRes = await fetch('/pow/target');
        if (!targetRes.ok) {
          throw new Error('Failed to fetch mining challenge.');
        }
        const challenge = await targetRes.json();

        setMiningStatus('mining');

        // 2. Start the Web Worker to solve the challenge
        const worker = new Worker('/powWorker.js?v=1');

        worker.postMessage({
          ...challenge,
          pubkey: getUserPubkeyHex(),      // <-- '0'.repeat(64) yerine bu
          apiBase: POW_API                 // <-- window.location.origin yerine bu
        });

        worker.onmessage = async (event) => {
          worker.terminate();
          const { ok, error, nonce, hash } = event.data;

          if (ok) {
            setMiningStatus('submitting');
            
            // 3. Submit the solved block to the main backend
            const globalIdx = (blockData[index] && typeof blockData[index].index === 'number') ? blockData[index].index : index;
            const response = await withRetry(`${API_BASE}/grid/${globalIdx}`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: {
                dugBy: username,
                visual: null,
                height: challenge.height,
                nonce: nonce,
                hash: hash
              }
            }, 1);
            
            if (!response.ok) {
              const result = await response.json();
               if (response.status === 401 || result.error === 'Unauthorized: Invalid or missing session token') {
                alert('Your session has expired. Please log in again.');
                localStorage.removeItem('session_token');
                localStorage.removeItem('username');
                localStorage.removeItem(`color_${username}`);
                window.location.reload();
                return;
              }
              if (response.status === 429) {
                alert(result?.error || 'Daily mining limit reached');
                // Show sad emoji for daily limit failure
                setDailyLimitFailedBlocks(prev => new Set(prev).add(index));
                setTimeout(() => {
                  setDailyLimitFailedBlocks(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(index);
                    return newSet;
                  });
                }, 6000);
                return;
              }
              throw new Error(result.error || "Submitting block failed");
            }

            setMiningStatus('success');
            applyDigSuccess(index, globalIdx);
            await fetchGrid(); // Refresh grid state

          } else {
            throw new Error(error || 'Mining worker failed.');
          }
        };

        worker.onerror = (err) => {
          worker.terminate();
          throw err;
        };

      } catch (error) {
        console.error("Mining process failed:", error);
        alert(`Mining failed: ${error instanceof Error ? error.message : String(error)}`);
        setMiningStatus('error');
      } finally {
        // Reset status after a delay to show success/error message
        setTimeout(() => {
          setIsMining(false);
          setMiningStatus('idle');
        }, 2000);
      }
    } else {
      // Original 10-second timer logic
      if (blockStates[index] !== "idle") return;

      setShowBlockModal(false);
      setIsMining(true);
      const newStates = [...blockStates];
      newStates[index] = "digging";
      setBlockStates(newStates);

      try {
        await new Promise((resolve) => setTimeout(resolve, 3000));

        const globalIdx = (blockData[index] && typeof blockData[index].index === 'number') ? blockData[index].index : index;
        const response = await withRetry(`${API_BASE}/grid/${globalIdx}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: {
            dugBy: username,
            visual: null,
          },
        }, 1);

        if (!response.ok) {
          const result = await response.json();
          if (response.status === 401 || result.error === 'Unauthorized: Invalid or missing session token') {
            alert('Your session has expired. Please log in again.');
            localStorage.removeItem('session_token');
            localStorage.removeItem('username');
            localStorage.removeItem(`color_${username}`);
            window.location.reload();
            return;
          }
          if (response.status === 429) {
            alert(result?.error || 'Daily mining limit reached');
            // Show sad emoji for daily limit failure
            setDailyLimitFailedBlocks(prev => new Set(prev).add(index));
            setTimeout(() => {
              setDailyLimitFailedBlocks(prev => {
                const newSet = new Set(prev);
                newSet.delete(index);
                return newSet;
              });
            }, 6000);
            newStates[index] = "idle";
            setBlockStates(newStates);
            return;
          }
          alert(result.error || "Digging failed");
          newStates[index] = "idle";
          setBlockStates(newStates);
          await fetchGrid();
        } else {
          applyDigSuccess(index, globalIdx);
          await fetchGrid();

          // >>> shadow PoW (opsiyonel) - DISABLED
          // kickPowShadow(getUserPubkeyHex());
        }
      } catch (error) {
        console.error("Failed to save digging:", error);
        alert("Digging failed");
        newStates[index] = "idle";
        setBlockStates(newStates);
        await fetchGrid();
      } finally {
        setIsMining(false);
      }
    }
  };


  const selectedBlock = selectedBlockIndex !== null ? blockData[selectedBlockIndex] : null;
  const selectedBlockState = selectedBlockIndex !== null ? blockStates[selectedBlockIndex] : null;
  const isOwner = selectedBlock?.dugBy === username;
  const isEmpty = selectedBlockState === "idle";

  return (
    <>
      <div style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '0 env(safe-area-inset-right) 0 env(safe-area-inset-left)'
      }}>
        {isGridLoading || blockData.length === 0 ? (
          <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }} />
        ) : (
        <>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <div className="grid-container" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(10, 1fr)',
            gap: '1px',
            width: '100%',
            maxWidth: '400px',
            margin: '0 auto',
            padding: '0',
            boxSizing: 'border-box',
            contain: 'layout style paint',
            willChange: isIOS ? 'auto' : 'contents',
            backgroundColor: 'transparent'
          }}>
          {blockStates.map((state, index) => {
          const block = blockData[index];
          const isUserBlock = block?.dugBy === username;
          const blockColor = state === 'dug'
            ? (
                // Prefer server-provided color for immediate paint
                (block?.color && typeof block.color === 'string' && block.color) ||
                (block?.dugBy === username ? userColor : (block?.dugBy && userColors[block.dugBy] ? userColors[block.dugBy] : 'transparent'))
              )
            : 'transparent';
          const bgColor = state === "dug" ? blockColor : "#fff";
          
          let visualContent = "";
          if (state === 'digging' || (isMining && selectedBlockIndex === index)) {
            if (USE_POW_MINING) {
              switch (miningStatus) {
                case 'fetching_challenge': visualContent = '⚙️'; break;
                case 'mining': visualContent = '⛏️'; break;
                case 'submitting': visualContent = '🔗'; break;
                case 'success': visualContent = '✅'; break;
                case 'error': visualContent = '❌'; break;
                default: visualContent = '⏳';
              }
            } else {
              visualContent = '⏳';
            }
          }

          return (
            <div
              ref={el => { blockRefs.current[index] = el; }}
              key={index}
              className={`grid-block ${state}${isUserBlock ? " my-block" : ""}`}
              title={
                state === "dug"
                  ? `Block #${block?.index} - Dug by: ${block?.dugBy || "Unknown"}`
                  : `Block #${block?.index}`
              }
              style={{
                backgroundColor: bgColor,
                width: '100%',
                height: '100%',
                aspectRatio: '1',
                minWidth: 0,
                minHeight: 0
              }}
              onClick={() => handleBlockClick(index)}
            >
              <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {visualContent}
                {newlyDugBlocks.has(block?.index ?? index) && (
                  <div className="newly-dug-visual">😊</div>
                )}
                {dailyLimitFailedBlocks.has(index) && (
                  <div className="daily-limit-failed-visual">😞</div>
                )}
              </div>
            </div>
          );
        })}
          </div>
        </div>
        {/* Sentinel for infinite scroll trigger */}
        <div ref={sentinelRef} style={{ width: 1, height: 1, margin: '0 auto', alignSelf: 'center', flex: '0 0 auto' }} />
        </>
        )}
      </div>

      {/* Block Info Modal - Rendered via Portal to body */}
      {showBlockModal && selectedBlock && selectedBlockIndex !== null && createPortal(
        <div 
          className="block-modal-overlay" 
          data-modal="block-info"
          data-safari={isSafari ? "true" : "false"}
          onClick={() => setShowBlockModal(false)}
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            bottom: '0',
            right: '0',
            zIndex: 2147483647,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            transform: 'none',
            transformOrigin: '0 0',
            zoom: 1,
            WebkitTransform: 'none',
            MozTransform: 'none',
            msTransform: 'none',
          }}
        >
          <div 
            className="block-modal-content" 
            data-modal-content="block-info"
            data-safari={isSafari ? "true" : "false"}
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              textAlign: 'center',
              width: getModalWidth(),
              maxWidth: getModalWidth(),
              minWidth: getModalWidth(),
              height: 'auto',
              maxHeight: '400px',
              position: 'relative',
              transform: 'none',
              transformOrigin: 'center center',
              zoom: 1,
              fontSize: '14px',
              WebkitTransform: 'none',
              MozTransform: 'none',
              msTransform: 'none',
              margin: '0',
              border: 'none',
              outline: 'none',
            }}
          >
            <button
              className="modal-close-x"
              onClick={() => setShowBlockModal(false)}
              aria-label="Close"
              style={{ position: 'absolute', top: 8, right: 8 }}
            >
              ×
            </button>
            <h3 style={{ margin: '0 0 16px 0', color: '#333', fontSize: '20px' }}>
              Block #{selectedBlock.index}
            </h3>
            {isEmpty ? (
              <p style={{ margin: '8px 0 20px 0', color: '#666', fontSize: '16px' }}>Empty Block</p>
            ) : isOwner ? (
              <>
                <p style={{ margin: '8px 0 12px 0', color: '#666', fontSize: '16px' }}>Owner: You</p>
                <div style={{ 
                  background: 'linear-gradient(45deg, #f8f9fa, #e3f2fd)', 
                  padding: '8px 12px', 
                  borderRadius: '6px', 
                  margin: '0 0 16px 0',
                  fontSize: '12px',
                  border: '1px solid #e1e8ed'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '4px' }}>
                    <span>⛓️</span>
                    <span style={{ fontWeight: '600', color: '#1976d2' }}>VolChain Record</span>
                  </div>
                  <div style={{ color: '#666', lineHeight: '1.3' }}>
                    <div>🔢 Dig ID: dig:{selectedBlock.dugBy}:{selectedBlock.index}</div>
                    <div>💰 Mined: 1 Volore</div>
                    <div dangerouslySetInnerHTML={{ __html: `🔗 ${blockInfo || 'Loading...'}` }}></div>
                    <div>✅ On-chain confirmed</div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p style={{ margin: '8px 0 12px 0', color: '#666', fontSize: '16px' }}>Owner: {selectedBlock.dugBy}</p>
                <div style={{ 
                  background: 'linear-gradient(45deg, #f8f9fa, #e3f2fd)', 
                  padding: '8px 12px', 
                  borderRadius: '6px', 
                  margin: '0 0 16px 0',
                  fontSize: '12px',
                  border: '1px solid #e1e8ed'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', marginBottom: '4px' }}>
                    <span>⛓️</span>
                    <span style={{ fontWeight: '600', color: '#1976d2' }}>VolChain Record</span>
                  </div>
                  <div style={{ color: '#666', lineHeight: '1.3' }}>
                    <div>🔢 Dig ID: dig:{selectedBlock.dugBy}:{selectedBlock.index}</div>
                    <div>💰 Mined: 1 Volore</div>
                    <div dangerouslySetInnerHTML={{ __html: `🔗 ${blockInfo || 'Loading...'}` }}></div>
                    <div>✅ On-chain confirmed</div>
                  </div>
                </div>
              </>
            )}
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {isEmpty && (
                <button 
                  className="block-modal-button dig-button" 
                  onClick={() => handleDigBlock(selectedBlockIndex)}
                  disabled={isMining}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    minWidth: '80px',
                  }}
                >
                  {isMining ? (USE_POW_MINING ? `Mining... (${miningStatus})` : "Mining...") : "Dig"}
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Grid;